import React, { useState, useEffect, useMemo } from 'react';
import { usePhase } from '../../../contexts/PhaseContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Class as ClassIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';

const AddClassPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { activePhase } = usePhase();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  
  // Single class form data
  const levelOptions = useMemo(() => {
    if (activePhase === 'sec') {
      return [
        { value: 'first_cycle', label: t('classManagement.add.levels.firstCycle') },
        { value: 'second_cycle', label: t('classManagement.add.levels.secondCycle') }
      ];
    }
    if (activePhase === 'nur_pri') {
      return [
        { value: 'nursery', label: t('classManagement.add.levels.nursery') },
        { value: 'primary', label: t('classManagement.add.levels.primary') }
      ];
    }
    return [];
  }, [activePhase, t]);

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    isActive: true,
    isExaminationClass: false
  });

  // Batch form data
  const [batchClasses, setBatchClasses] = useState([]);
  const [batchFormData, setBatchFormData] = useState({
    isActive: true
  });
  const [bulkFile, setBulkFile] = useState(null);

  useEffect(() => {
    setBatchClasses([{ name: '', level: '', isExaminationClass: false }]);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBatchInputChange = (field, value) => {
    setBatchFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBatchClassChange = (index, field, value) => {
    const updatedClasses = [...batchClasses];
    updatedClasses[index][field] = value;
    setBatchClasses(updatedClasses);
  };

  const addBatchClass = () => {
    setBatchClasses([...batchClasses, { name: '', level: '', isExaminationClass: false }]);
  };

  const removeBatchClass = (index) => {
    if (batchClasses.length > 1) {
      const updatedClasses = batchClasses.filter((_, i) => i !== index);
      setBatchClasses(updatedClasses);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      enqueueSnackbar(t('classManagement.add.validation.fillRequired'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const payload = {
        ...formData,
        level: formData.level || null,
        hasSubjectSelection: activePhase === 'sec' ? !!formData.isExaminationClass : false,
        isExaminationClass: activePhase === 'sec' ? !!formData.isExaminationClass : false,
        schoolId: schoolId ? Number(schoolId) : undefined,
        phaseId: phaseId ? Number(phaseId) : undefined,
      };
      const response = await fetchJson('/api/classes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      enqueueSnackbar(t('classManagement.add.toasts.singleSuccess'), { variant: 'success' });
      
      // Reset form and show success message
      setFormData({
        name: '',
        level: '',
        isActive: true,
        isExaminationClass: false
      });
      
      // Navigate back after a short delay to show the success message
      setTimeout(() => {
        navigate('/class-management');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating class:', error);
      const msg1 = error?.userMessage || (error?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || 'An unexpected error occurred';
      setError(t('classManagement.add.errors.createSingle', { message: msg1 }));
      enqueueSnackbar(t('classManagement.add.toasts.singleError', { message: msg1 }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    
    const validClasses = batchClasses.filter(cls => cls.name.trim());
    if (validClasses.length === 0) {
      enqueueSnackbar(t('classManagement.add.validation.addOneClass'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const classesToCreate = validClasses.map(cls => ({
        ...cls,
        level: cls.level || null,
        isActive: batchFormData.isActive,
        hasSubjectSelection: activePhase === 'sec' ? !!cls.isExaminationClass : false,
        isExaminationClass: activePhase === 'sec' ? !!cls.isExaminationClass : false,
        schoolId: schoolId ? Number(schoolId) : undefined,
        phaseId: phaseId ? Number(phaseId) : undefined,
      }));

      // Create classes one by one
      for (const classData of classesToCreate) {
                  await fetchJson('/api/classes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(classData)
        });
      }

      enqueueSnackbar(t('classManagement.add.toasts.batchSuccess', { count: validClasses.length }), { variant: 'success' });
      
      // Reset batch form and show success message
      setBatchFormData({
        isActive: true
      });
      setBatchClasses([{ name: '', level: '', isExaminationClass: false }]);
      
      // Navigate back after a short delay to show the success message
      setTimeout(() => {
        navigate('/class-management');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating classes:', error);
      const msg2 = error?.userMessage || (error?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || 'An unexpected error occurred';
      setError(t('classManagement.add.errors.createBatch', { message: msg2 }));
      enqueueSnackbar(t('classManagement.add.toasts.batchError', { message: msg2 }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/class-management');
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBulkFile(file);
    if (event.target) event.target.value = '';
  };

  const downloadTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const response = await fetch(resolveApiUrl('/api/classes/template'), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
          ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
          ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {})
        },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Template download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'class_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error downloading class template:', downloadError);
      const dlMsg = downloadError?.userMessage || (downloadError?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || 'Failed to download template';
      enqueueSnackbar(dlMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkFile) {
      enqueueSnackbar(t('classManagement.add.validation.addOneClass'), { variant: 'error' });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const form = new FormData();
      form.append('file', bulkFile);
      const data = await fetchJson('/api/classes/import?preview=1', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
          ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
          ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {})
        },
        body: form
      });
      const previewErrors = Array.isArray(data?.errors) ? data.errors : [];
      if (previewErrors.length) {
        throw new Error(previewErrors.map((item) => `Row ${item.row}: ${item.error}`).join(' | '));
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      if (!items.length) {
        throw new Error(t('classManagement.add.validation.addOneClass'));
      }
      setBatchClasses(items.map((item) => ({
        name: item?.name || '',
        level: item?.level || '',
        isExaminationClass: !!(item?.isExaminationClass || item?.hasSubjectSelection)
      })));
      setBulkFile(null);
      setActiveTab(1);
      enqueueSnackbar(t('classManagement.add.toasts.batchSuccess', { count: items.length }), { variant: 'success' });
    } catch (importError) {
      console.error('Error importing classes:', importError);
      const impMsg = importError?.userMessage || (importError?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || 'Failed to import classes';
      setError(impMsg);
      enqueueSnackbar(impMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          {t('classManagement.add.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('classManagement.add.pageTitle')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('classManagement.add.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={t('classManagement.add.tabs.single')} />
          <Tab label={t('classManagement.add.tabs.batch')} />
          <Tab label={t('classManagement.add.tabs.bulk')} />
        </Tabs>
      </Paper>

      {/* Single Class Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('classManagement.add.single.title')}
              </Typography>
              
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('classManagement.add.single.fields.name')}
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={t('classManagement.add.single.fields.namePlaceholder')}
                      required
                    />
                  </Grid>

                  {levelOptions.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>{t('classManagement.add.single.fields.level')}</InputLabel>
                        <Select
                          value={formData.level}
                          label={t('classManagement.add.single.fields.level')}
                          onChange={(e) => handleInputChange('level', e.target.value)}
                        >
                          {levelOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        />
                      }
                      label={t('classManagement.add.single.fields.active')}
                    />
                  </Grid>

                  {activePhase === 'sec' && (
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isExaminationClass}
                            onChange={(e) => handleInputChange('isExaminationClass', e.target.checked)}
                          />
                        }
                        label={t('classManagement.add.single.fields.subjectSelectionRequired')}
                      />
                    </Grid>
                  )}

                </Grid>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {t('classManagement.add.single.buttons.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    {loading
                      ? t('classManagement.add.single.buttons.creating')
                      : t('classManagement.add.single.buttons.submit')}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ClassIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('classManagement.add.single.tips.title')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.single.tips.tip1')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.single.tips.tip2')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.single.tips.tip3')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.single.tips.tip4')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Batch Classes Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('classManagement.add.batch.title')}
              </Typography>
              
              <form onSubmit={handleBatchSubmit}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={batchFormData.isActive}
                          onChange={(e) => handleBatchInputChange('isActive', e.target.checked)}
                        />
                      }
                      label={t('classManagement.add.batch.fields.active')}
                    />
                  </Grid>

                </Grid>

                <Box sx={{ mb: 2 }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addBatchClass}
                    variant="outlined"
                  >
                    {t('classManagement.add.batch.actions.addAnother')}
                  </Button>
                </Box>

                <List>
                  {batchClasses.map((cls, index) => (
                    <ListItem key={index} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label={t('classManagement.add.batch.fields.className')}
                            value={cls.name}
                            onChange={(e) => handleBatchClassChange(index, 'name', e.target.value)}
                            placeholder={t('classManagement.add.batch.fields.classNamePlaceholder')}
                            required
                          />
                        </Grid>
                        {levelOptions.length > 0 && (
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth required>
                              <InputLabel>{t('classManagement.add.batch.fields.level')}</InputLabel>
                              <Select
                                value={cls.level}
                                label={t('classManagement.add.batch.fields.level')}
                                onChange={(e) => handleBatchClassChange(index, 'level', e.target.value)}
                              >
                                {levelOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {activePhase === 'sec' && (
                          <Grid item xs={12} md={3}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!!cls.isExaminationClass}
                                  onChange={(e) => handleBatchClassChange(index, 'isExaminationClass', e.target.checked)}
                                />
                              }
                              label={t('classManagement.add.batch.fields.subjectSelectionRequired')}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={1}>
                          <IconButton
                            onClick={() => removeBatchClass(index)}
                            disabled={batchClasses.length === 1}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 3 }} />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {t('classManagement.add.batch.buttons.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    {loading
                      ? t('classManagement.add.batch.buttons.creating')
                      : t('classManagement.add.batch.buttons.submit', {
                          count: batchClasses.filter(c => c.name.trim()).length,
                        })}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ClassIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('classManagement.add.batch.tips.title')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.batch.tips.tip1')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.batch.tips.tip2')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.batch.tips.tip3')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('classManagement.add.batch.tips.tip4')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bulk Import Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('classManagement.add.bulk.title')}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  variant="outlined"
                  sx={{ mr: 2 }}
                >
                  {t('classManagement.add.bulk.downloadTemplate')}
                </Button>
                <Button
                  startIcon={<UploadIcon />}
                  variant="contained"
                  component="label"
                >
                  {t('classManagement.add.bulk.uploadFile')}
                  <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleBulkFileChange} />
                </Button>
              </Box>

              {bulkFile && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {bulkFile.name}
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{t('classManagement.add.bulk.importGuidelinesTitle')}</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  {t('classManagement.add.bulk.guidelines.line1')}
                  <br />
                  {t('classManagement.add.bulk.guidelines.line2')}
                  <br />
                  {t('classManagement.add.bulk.guidelines.line3')}
                  <br />
                  {t('classManagement.add.bulk.guidelines.line4')}
                  <br />
                  {t('classManagement.add.bulk.guidelines.line5')}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  {t('classManagement.add.bulk.buttons.cancel')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  disabled={loading || !bulkFile}
                  onClick={handleBulkImport}
                >
                  {t('classManagement.add.bulk.buttons.import')}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('classManagement.add.bulk.sidebar.title')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.bulk.sidebar.tip1')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.bulk.sidebar.tip2')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('classManagement.add.bulk.sidebar.tip3')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('classManagement.add.bulk.sidebar.tip4')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AddClassPage;
